import type { ReactNode } from "react";
import { cx } from "@/utils/cx";

/** Rail marker beside a step. */
export type PolicyMarker =
  /** Solid dark circle with a white icon — a start or terminal point. */
  | { type: "icon"; icon: ReactNode }
  /** Solid dark circle with the step's ordinal. */
  | { type: "step"; number: number }
  /** Subtle white circle with a gray icon — a passive step such as a wait. */
  | { type: "muted"; icon: ReactNode };

const markerBase = "relative z-10 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full border";
const markerTone = {
  solid: "border-gray-300 bg-gray-600 text-white",
  subtle: "border-gray-200 bg-white text-gray-600",
} as const;

export function PolicyRailMarker({ marker }: { marker: PolicyMarker }) {
  if (marker.type === "step") {
    return (
      <span className={cx(markerBase, markerTone.solid)}>
        <span className="text-sm leading-5 font-semibold">{marker.number}</span>
      </span>
    );
  }
  const solid = marker.type === "icon";
  return (
    <span className={cx(markerBase, solid ? markerTone.solid : markerTone.subtle)}>
      <span className={solid ? "[&>svg]:size-4" : "[&>svg]:size-[18px]"}>{marker.icon}</span>
    </span>
  );
}

/**
 * Which canvas the cards sit on. "cream" tints them on a white panel; "white" inverts it for a
 * cream canvas, so they still read as objects rather than blending into the background.
 */
export type PolicySurface = "cream" | "white";

const cardSurface: Record<PolicySurface, { header: string; body: string }> = {
  cream: { header: "bg-unbox-cream-50", body: "bg-unbox-cream-25" },
  white: { header: "bg-white", body: "bg-gray-50" },
};

export interface PolicyDelayPillProps {
  verb?: string;
  /** The amount chip — a number when read-only, or an input when editable. */
  amount: ReactNode;
  unit: string;
  /** Trailing controls, e.g. a remove menu. */
  actions?: ReactNode;
  surface?: PolicySurface;
}

/** Wait pill — "Wait 3 business days", with the amount in its own round chip. */
export function PolicyDelayPill({ verb = "Wait", amount, unit, actions, surface = "cream" }: PolicyDelayPillProps) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 self-start rounded-md border border-gray-200 py-2 pl-4",
        surface === "cream" ? "bg-unbox-cream-25" : "bg-white",
        actions ? "pr-1" : "pr-4",
      )}
    >
      <span className="text-xs leading-[18px] font-medium text-primary">{verb}</span>
      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-gray-200 bg-white px-1 text-xs leading-[18px] font-bold text-primary">
        {amount}
      </span>
      <span className="text-xs leading-[18px] font-medium text-primary">{unit}</span>
      {actions && <span className="ml-1 flex shrink-0 items-center">{actions}</span>}
    </span>
  );
}

export interface PolicyActionCardProps {
  /** Leading 24px icon. Omit for a terminal card such as "Automation complete". */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Expanded panel under the header — e.g. a message body preview. */
  body?: { label?: ReactNode; text: ReactNode };
  /** "md" renders the title at 16px — terminal cards. */
  size?: "sm" | "md";
  /** Trailing controls, e.g. an edit/remove menu. */
  actions?: ReactNode;
  surface?: PolicySurface;
  className?: string;
}

/** Tinted item card — the unit of an automation step. */
export function PolicyActionCard({
  icon,
  title,
  description,
  body,
  size = "sm",
  actions,
  surface = "cream",
  className,
}: PolicyActionCardProps) {
  const header = (
    <div
      className={cx(
        "flex w-full items-center gap-3 border-gray-200 px-4 py-3",
        cardSurface[surface].header,
        body ? "rounded-t-md border-t border-r border-l" : "rounded-md border",
        !body && className,
      )}
    >
      {icon && <span className="shrink-0 text-gray-600 [&>svg]:size-6">{icon}</span>}
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span
          className={cx(
            "truncate text-primary",
            size === "md" ? "text-base leading-6 font-medium" : "text-sm leading-5 font-semibold",
          )}
        >
          {title}
        </span>
        {description && <span className="truncate text-sm leading-5 text-tertiary">{description}</span>}
      </div>
      {actions && <span className="flex shrink-0 items-center">{actions}</span>}
    </div>
  );

  if (!body) return header;

  return (
    <div className={cx("flex w-full flex-col", className)}>
      {header}
      <div
        className={cx("flex w-full flex-col gap-1 rounded-b-md border border-gray-200 p-4", cardSurface[surface].body)}
      >
        {body.label && (
          <span className="truncate text-xs leading-[18px] font-medium text-quaternary">{body.label}</span>
        )}
        <p className="text-sm leading-5 font-medium whitespace-pre-wrap text-tertiary">{body.text}</p>
      </div>
    </div>
  );
}

export interface PolicyStepRowProps {
  marker?: PolicyMarker;
  /** Cards beside the marker. A stack hangs off the marker; a lone card centres on it. */
  children: ReactNode;
  /** Force the stacked (top-aligned) layout, e.g. while a card has an expanded body. */
  stacked?: boolean;
  className?: string;
}

/** One rail row: a marker beside its card(s). */
export function PolicyStepRow({ marker, children, stacked = false, className }: PolicyStepRowProps) {
  return (
    <div className={cx("flex w-full gap-3 py-2", stacked ? "items-start" : "items-center", className)}>
      <div className={cx("flex shrink-0 items-center", stacked && "py-2")}>
        {marker ? <PolicyRailMarker marker={marker} /> : <span className="size-8" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">{children}</div>
    </div>
  );
}

export interface PolicyRailProps {
  /** Draws the vertical connector behind the markers. */
  railed?: boolean;
  children: ReactNode;
  className?: string;
}

/** Positions the vertical rail behind a run of `PolicyStepRow`s. */
export function PolicyRail({ railed = true, children, className }: PolicyRailProps) {
  return (
    <div className={cx("relative flex w-full flex-col", className)}>
      {railed && <span className="absolute top-8 bottom-7 left-4 w-px bg-gray-200" />}
      {children}
    </div>
  );
}

const dividerSizes = {
  sm: { row: "h-5", label: "text-sm leading-5 font-medium text-quaternary" },
  md: { row: "h-6", label: "text-base leading-6 font-semibold text-gray-700" },
} as const;

export interface PolicySectionDividerProps {
  label: string;
  /** "md" reads as a section heading rather than a feed label. */
  size?: "sm" | "md";
  /** Set false for a bare label, with no hairline running to the right edge. */
  rule?: boolean;
  className?: string;
}

/** Section divider — label plus an optional hairline to the right edge. */
export function PolicySectionDivider({ label, size = "sm", rule = true, className }: PolicySectionDividerProps) {
  return (
    <div className={cx("flex w-full items-center gap-4", dividerSizes[size].row, className)}>
      <span className={cx("shrink-0", dividerSizes[size].label)}>{label}</span>
      {rule && <span aria-hidden className="h-px flex-1 bg-gray-200" />}
    </div>
  );
}
